import { FirebaseError } from 'firebase/app';
import {
  GoogleAuthProvider,
  onIdTokenChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { firebaseConfigured } from '../lib/env';
import { auth } from '../lib/firebase';

/**
 * Firebase Auth, wrapped so the rest of the app never imports it directly.
 * This only answers "who is holding this browser". Whether they are an admin
 * is the API's call (see AdminGate in router.tsx). There is deliberately no
 * signup here: an admin account is made by another admin, not by a form.
 */

export interface AuthContextValue {
  user: User | null;
  /** True until Firebase has restored (or ruled out) a persisted session. */
  loading: boolean;
  configured: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  sendReset: (email: string) => Promise<void>;
  /** For a password account that has not proven its address yet. */
  sendVerification: () => Promise<void>;
  /** Re-read the account from Firebase, after a verification link was clicked. */
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Firebase's error codes are not sentences. These are. */
export function authErrorMessage(error: unknown): string {
  const code = error instanceof FirebaseError ? error.code : '';
  switch (code) {
    case 'auth/invalid-email':
      return 'That does not look like an email address.';
    case 'auth/missing-password':
      return 'Enter your password.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'That email and password do not match an account.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a minute and try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'The Google sign-in window closed before finishing.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the Google sign-in window. Allow popups for this site and try again.';
    case 'auth/network-request-failed':
      return 'Could not reach the sign-in service. Check your connection.';
    case 'auth/operation-not-allowed':
      return 'That sign-in method is not enabled for this project.';
    default:
      if (error instanceof Error && error.message) return error.message;
      return 'Sign in failed. Try again.';
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(firebaseConfigured);

  useEffect(() => {
    if (!firebaseConfigured) return;
    // onIdTokenChanged rather than onAuthStateChanged: it also fires on token
    // refresh, which keeps `user.getIdToken()` cheap for the API client.
    return onIdTokenChanged(auth, (next) => {
      setUser(next && !next.isAnonymous ? next : null);
      setLoading(false);
    });
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    await signInWithPopup(auth, provider);
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email.trim(), password);
  }, []);

  const sendReset = useCallback(async (email: string) => {
    await sendPasswordResetEmail(auth, email.trim());
  }, []);

  const sendVerification = useCallback(async () => {
    if (auth.currentUser) await sendEmailVerification(auth.currentUser);
  }, []);

  const refresh = useCallback(async () => {
    const current = auth.currentUser;
    if (!current) return;
    await current.reload();
    // The API reads email_verified from the ID token, which only picks up the
    // change after a forced refresh.
    await current.getIdToken(true);
    setUser(auth.currentUser);
  }, []);

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      configured: firebaseConfigured,
      signInWithGoogle,
      signInWithEmail,
      sendReset,
      sendVerification,
      refresh,
      signOut,
    }),
    [loading, refresh, sendReset, sendVerification, signInWithEmail, signInWithGoogle, signOut, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>.');
  return value;
}

/** Best available human name for the signed-in person. */
export function displayNameOf(user: User | null): string {
  if (!user) return '';
  return user.displayName?.trim() || user.email?.split('@')[0] || 'You';
}
